import { useEffect, useRef, useCallback, useReducer } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { AUTH_DISABLED } from 'src/App/Providers';

// Matches the serialized shape from unwind.gateway.ts
export interface UnwindOrderState {
  ruleId: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
}

export interface UnwindExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface UnwindErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: UnwindOrderState[] }
  | { type: 'EXECUTION'; payload: UnwindExecutionEvent }
  | { type: 'ERROR'; payload: UnwindErrorEvent };

interface State {
  /** Map of ruleId -> live order state */
  states: Map<string, UnwindOrderState>;
  /** Most recent execution event (for toasts/notifications) */
  lastExecution: UnwindExecutionEvent | null;
  /** Most recent error event */
  lastError: UnwindErrorEvent | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      const newMap = new Map<string, UnwindOrderState>();
      for (const s of action.payload) {
        newMap.set(s.ruleId, s);
      }
      return { ...state, states: newMap };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    default:
      return state;
  }
}

export function useUnwindingSocket() {
  const accessToken = _getToken();

  const { socket } = useSocket(
    import.meta.env.VITE_POSITION_UNWINDER_URL + '/unwind',
    {
      enabled: AUTH_DISABLED || !!accessToken,
      auth: { token: accessToken },
    }
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // Subscribe on connect
  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }
    if (!hasSubscribed.current) {
      socket.emit('subscribe_unwind');
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  // Listen to events
  const { lastMessage: statusMessage } = useSocketEvent<UnwindOrderState[]>(
    socket,
    'unwind_status'
  );

  const { lastMessage: executionMessage } =
    useSocketEvent<UnwindExecutionEvent>(socket, 'unwind_execution');

  const { lastMessage: errorMessage } = useSocketEvent<UnwindErrorEvent>(
    socket,
    'unwind_error'
  );

  useEffect(() => {
    if (statusMessage) {
      dispatch({ type: 'STATUS', payload: statusMessage });
    }
  }, [statusMessage]);

  useEffect(() => {
    if (executionMessage) {
      dispatch({ type: 'EXECUTION', payload: executionMessage });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      dispatch({ type: 'ERROR', payload: errorMessage });
    }
  }, [errorMessage]);

  const getState = useCallback(
    (ruleId: string): UnwindOrderState | undefined => state.states.get(ruleId),
    [state.states]
  );

  return {
    isConnected: socket.connected,
    states: state.states,
    getState,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
  };
}
